import {
  FetchUserWorkspaceMemberships,
  GetCurrentUserWorkspaceMemberships,
} from 'iam/WorkspaceMembershipStore'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import {
  FilterItem,
  FilterItemSource,
  FilterStorage,
  FiltersViewModel,
} from 'infra/FiltersViewModel'
import { observable, computed, makeObservable, action } from 'mobx'
import { task } from 'mobx-task'
import { toStream, tapAction } from 'utils/mobx-util'
import {
  Subscription,
  combineLatest,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs'
import { CursorPageQuery, Id } from 'types/definitions'
import { match } from 'utils/pattern-match-util'
import { SearchableBookkeepingManagerSortField } from './BookkeepingManagerTableColumn'
import { TableSortOrder } from 'components/Table'
import { identity, omit, remove, uniq } from 'lodash'
import { areDeepEqual } from 'utils/object-util'
import { shareLatest } from 'utils/rx-util'
import BookkeepingPendingAction, {
  BookkeepingManagerAction,
} from './BookkeepingPendingAction'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import BookkeepingManagerStore, {
  SearchBookkeepingPendingActionRequest,
} from 'admin/BookkeepingManagerStore'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'

/**
 * The action filter options.
 */
export type BookkeepingManagerActionFilterOption = 'all' | 'confirm_access'

/**
 * The action filter.
 */
export interface BookkeepingManagerActionFilter {
  count?: number
  type: BookkeepingManagerActionFilterOption
}
/**
 * Predefined instance of BookkeepingManagerActionFilter.
 */
const BookkeepingManagerActionFilter = {
  all: {
    count: 0,
    type: 'all',
  } as BookkeepingManagerActionFilter,
  confirmAccess: {
    count: 0,
    type: 'confirm_access',
  } as BookkeepingManagerActionFilter,
}

/**
 * The filters used in the view model filter.
 */
export type BookkeepingManagerFilter = {
  workspaceIds: Id[]
  action: BookkeepingManagerActionFilter
}

/**
 * The sort fields.
 */
export interface BookkeepingManagerSort {
  field: SearchableBookkeepingManagerSortField
  direction: TableSortOrder
}

/**
 * The search filters.
 */
export type BookkeepingManagerSearchFilter = {
  workspaceIds: Id[]
  action?: BookkeepingManagerAction
  sort: BookkeepingManagerSort
  searchText: string
}

/**
 * The search result.
 */
export interface BookkeepingManagerSearchResult {
  searchFilter: BookkeepingManagerSearchFilter
  filtersApplied: boolean
  readonly bookkeepingPendingActions: readonly BookkeepingPendingAction[]
}

/**
 * Provides a predefined value and utility values to manage search filters.
 */
export const BookkeepingManagerSearchFilter = {
  none: {
    workspaceIds: [],
    sort: { field: 'date_pending_since', direction: 'desc' },
    searchText: '',
  } as BookkeepingManagerSearchFilter,

  isNone: (searchFilter: BookkeepingManagerSearchFilter) =>
    BookkeepingManagerSearchFilter.equals(
      omit(BookkeepingManagerSearchFilter.none, 'sort'),
      omit(searchFilter, 'sort'),
    ),

  equals: (
    left: Partial<BookkeepingManagerSearchFilter>,
    right: Partial<BookkeepingManagerSearchFilter>,
  ) => areDeepEqual(left, right),
}

export default class BookkeepingManagerSearchViewModel {
  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  /**
   * Backing field for the last search.
   */
  @observable.ref
  private lastSearch: BookkeepingManagerSearchResult = {
    searchFilter: BookkeepingManagerSearchFilter.none,
    filtersApplied: false,
    bookkeepingPendingActions: [],
  }

  /**
   * The bookkeeping pending actions scroller.
   */
  scroller = new InfiniteScrollProvider<
    BookkeepingPendingAction,
    BookkeepingManagerSearchFilter
  >({
    limit: 20,
    // We trigger the initial fetch ourselves. If this was false,
    // we would see 2 initial fetches.
    startFetchTaskAsPending: true,
    fetchItems: (paging, _, filter) =>
      this.fetchBookkeepingPendingActions(
        this.toBookkeepingPendingActionRequest(paging, filter),
      ),
  })

  /**
   * The bookkeeping pending actions filters.
   */
  filters = new FiltersViewModel<BookkeepingManagerFilter>({
    filterStorage: this.filterStorage,
    definitions: {
      workspaceIds: {
        title: 'Workspaces',
        implicit: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(this.getWorkspaces()),
            true,
          ),
      },
      action: {
        icon: 'pending',
        title: 'Quick Select',
        implicit: true,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            BookkeepingManagerActionFilter.all,
            this.getActionFilters.bind(this),
          ),
      },
    },
  })

  /*
   * The sorting field.
   */
  @observable
  sorting: BookkeepingManagerSort = {
    field: 'date_pending_since',
    direction: 'desc',
  }

  /*
   * The search text.
   */
  @observable
  searchText = new SearchTextViewModel()

  /*
   * The searches$ observable combines filter changes, sort changes, and debounced
   * search text inputs to produce a stream of BookkeepingManagerSearchFilter objects.
   * It then performs a search based on the current filter, sort, and search text,
   * and returns the search BookkeepingManagerSearchResult.
   */
  private searches$ = combineLatest([
    // ...with the remaining filter changes
    this.filters.appliedFilters$,
    // ...and the sort
    toStream(() => this.sorting, true),
    this.searchText.debouncedValue$,
  ]).pipe(
    map(
      ([appliedFilters, sort, searchText]): BookkeepingManagerSearchFilter => ({
        workspaceIds: appliedFilters.workspaceIds || [],
        action: this.toBookkeepingManagerAction(appliedFilters.action?.type),
        sort,
        searchText,
      }),
    ),
    // Ignore emissions with the same search filter.
    distinctUntilChanged<BookkeepingManagerSearchFilter>(
      BookkeepingManagerSearchFilter.equals,
    ),
    // Start a search, return the filter that was used.
    switchMap((query) => this.searchItems(query)),
    // Make sure to only subscribe once.
    shareLatest(),
  )

  constructor(
    private bookkeepingManagerStore: BookkeepingManagerStore,
    private fetchWorkspaceMemberships: FetchUserWorkspaceMemberships,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private filterStorage?: FilterStorage,
  ) {
    makeObservable(this)
  }

  /**
   * The workspace filter.
   */
  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspaceIds') as SelectFilter<string, Id>
  }

  /**
   * The action filter.
   */
  @computed
  get actionFilter() {
    return this.filters.getFilter('action') as SingleSelectFilter<
      string,
      BookkeepingManagerActionFilter
    >
  }

  /**
   * The bookkeeping pending actions filtered.
   */
  @computed
  get filteredItems() {
    return this.scroller.items
  }

  /**
   * The internal bookkeeping pending actions ran through the transformer.
   */
  @computed
  get bookkeepingPendingActions() {
    return identity(uniq(this.scroller.items))
  }

  /**
   * The total of bookkeeping pending actions.
   */
  @computed
  get total() {
    return (
      this.actionFilter.options.find((x) => x.value?.type === 'all')?.value
        .count ?? 0
    )
  }

  /**
   * Sets the sort method.
   */
  @action.bound
  setSortMethod(sortMethod: BookkeepingManagerSort) {
    this.sorting = sortMethod
  }

  /**
   * Sets the sorting direction.
   */
  @action.bound
  setSortDirection(direction: TableSortOrder) {
    // If we have no direction remove any sorting.
    if (direction === 'none') {
      this.setSortMethod({
        field: 'date_pending_since',
        direction: 'none',
      })
      return
    }

    this.setSortMethod({
      direction,
      field: this.sorting.field,
    })
  }

  /**
   * Removes bookkeeping pending action from the items.
   *
   * @param id
   */
  @action.bound
  removeBookkeepingPendingAction(id: Id) {
    const bookkeepingPendingAction = this.scroller.items.find(
      (x) => x.id === id,
    )

    if (bookkeepingPendingAction) {
      remove(this.scroller.items, bookkeepingPendingAction)
    }
  }

  /**
   * Sets the sort filed.
   */
  @action.bound
  setSortField(field: SearchableBookkeepingManagerSortField) {
    this.setSortMethod({
      field,
      direction: this.sorting.direction,
    })
  }

  /**
   * Gets the action filters.
   */
  async getActionFilters(): Promise<
    FilterItem<BookkeepingManagerActionFilter>[]
  > {
    const projection =
      await this.bookkeepingManagerStore.getBookkeepingPendingActionAggregations(
        this.workspaceFilter.value.map(Id.assert),
      )

    return [
      {
        title: 'Total requests',
        value: {
          type: 'all',
          count: projection.total,
        },
      },
      {
        title: 'Confirm access',
        value: {
          type: 'confirm_access',
          count: projection.confirmAccessTotal,
        },
      },
    ]
  }

  /**
   * Gets workspaces.
   */
  getWorkspaces(): FilterItem<Id>[] {
    return this.getWorkspaceMemberships()
      .filter((mb) => mb.member.hasSomePermissions('MANAGE_BOOKKEEPING'))
      .map((membership) => {
        return {
          title: membership.workspace.name,
          value: membership.workspace.id,
        }
      })
  }

  /**
   * Searches bookkeeping pending actions using the scroller.
   */
  private async searchItems(query: BookkeepingManagerSearchFilter) {
    await this.scroller.fetch(query)

    return {
      searchFilter: query,
      filtersApplied: BookkeepingManagerSearchFilter.isNone(query),
      bookkeepingPendingActions: [],
    }
  }

  /**
   * Maps manager action filter options to manager action.
   */
  private toBookkeepingManagerAction(
    action?: BookkeepingManagerActionFilterOption,
  ) {
    if (!action) {
      return undefined
    }

    return match(action, {
      confirm_access: () => BookkeepingManagerAction.ConfirmAccess,
      all: () => undefined,
    })
  }

  /**
   * Maps the paging and search filter to SearchBookkeepingPendingActionRequest.
   */
  private toBookkeepingPendingActionRequest(
    paging: CursorPageQuery,
    filter?: BookkeepingManagerSearchFilter,
  ): SearchBookkeepingPendingActionRequest {
    return {
      workspaceIds: filter?.workspaceIds.map(Id.assert),
      orderBy: `${filter?.sort.field.toString()} ${
        filter?.sort.direction !== 'none' ? filter?.sort.direction : ''
      }`,
      pageSize: paging.limit,
      pageToken: paging.after || undefined,
      action: filter?.action,
      searchText: filter?.searchText,
    }
  }

  /**
   * Fetches the bookkeeping pending actions.
   */
  private async fetchBookkeepingPendingActions(
    query: SearchBookkeepingPendingActionRequest,
  ) {
    return this.bookkeepingManagerStore.searchBookkeepingPendingActions(query)
  }

  /**
   * Activates the vm.
   */
  activate = task.resolved(async () => {
    this.subscription.add(
      this.bookkeepingManagerStore.events$.subscribe((_) => {
        this.scroller.fetch(this.lastSearch.searchFilter)
        this.actionFilter.loadOptions()
      }),
    )

    this.filters.activate()

    this.subscription.add(
      this.searches$
        .pipe(
          // Can't use fromStream because it will subscribe immediately,
          // which we don't want.
          tapAction((search) => (this.lastSearch = search)),
        )
        .subscribe(),
    )

    // let's clear search text and load the action filter only when the workspace changes.
    this.subscription.add(
      this.filters.appliedFilters$
        .pipe(
          map((a) => a.workspaceIds),
          distinctUntilChanged(),
          tapAction(async () => {
            this.searchText.clear()
            await this.actionFilter.loadOptions()
          }),
        )
        .subscribe(),
    )

    // ensure we has fetched the workspace memberships.
    await this.fetchWorkspaceMemberships()
    await this.workspaceFilter.loadOptions()
  })

  /**
   * Deactivates the vm.
   */
  deactivate() {
    this.filters.deactivate()
    this.subscription.unsubscribe()
  }
}
